import {
	DropOverContext,
	Icon,
	IconValue
} from "client/Components";
import {
	EmbedInputDialogData
} from "client/Features/Hub/HubMediaSelect/EmbedInputDialog";
import { HubItemElementKind } from "client/Api/Hub/common";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useContext,
	useState
} from "react";
import { BuilderContext } from "../../BuilderContext";
import { MenuList } from "./MenuList";
import { resolveFormattedTextString, getDataUrl } from "client/Misc/utils";
import { AlertManager, useAlerts } from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { VideoInputDialogData } from "client/Features/Hub/HubMediaSelect/VideoInputDialog";
import { setClientId } from "client/Misc/clientId";
import { HubItemElementFormModel } from "client/Features/Hub/Management/common";
import { fileMetadataSymbol } from "client/Misc/commonTypes";
import { SelectImageMenu } from "./SelectImageMenu";
import { never } from "client_legacy/ndaw/errors";
import { ImageWithAttribution, HubImageSelectorKind } from "client/Features/Hub/Management/useHubImageSelector";

export type ElementPickerProps = {
	tabbableIndex?: number;
	onSelect(
		element: HubItemElementFormModel
	): void;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
	onMenuItemsFocusReset: () => void;
};

export const ElementPicker = (props: ElementPickerProps): ReactElement => {
	const {
		tabbableIndex,
		onSelect,
		onOpenVideoInputDialog,
		onOpenEmbedInputDialog,
		onSelectImage,
		onMenuItemsFocusReset
	} = props;

	const [view, setView] = useState<ViewState>("default");
	const alertManager: AlertManager = useAlerts();

	const {
		onClose: closeContainingDropOver
	} = useContext(DropOverContext);

	const {
		imageRestrictions,
		isPixabayImageGalleryAvailable
	} = useContext(BuilderContext);

	const selectSubtitleHandler = useCallback(
		(): void => {
			closeContainingDropOver?.();

			onSelect({
				kind: HubItemElementKind.Subtitle,
				id: null,
				text: ""
			});
		},
		[onSelect, closeContainingDropOver]
	);

	const selectParagraphHandler = useCallback(
		(): void => {
			closeContainingDropOver?.();

			onSelect({
				kind: HubItemElementKind.Paragraph,
				id: null,
				html: ""
			});
		},
		[closeContainingDropOver, onSelect]
	);

	const selectImageHandler = useCallback(
		async (kind: HubImageSelectorKind): Promise<void> => {
			closeContainingDropOver?.();

			const result: TryResult<ImageWithAttribution> = await onSelectImage(kind);

			if (!result.wasSuccessful) {
				return;
			}

			const {
				file,
				attribution
			} = result.result;

			try {
				const dataUrl: string = await getDataUrl(file);

				if (file.size > imageRestrictions.maxImageSize) {
					alertManager.showError(
						resolveFormattedTextString({
							template: localize("_MaxSizeIs"),
							replacements: [
								{
									what: "{0}",
									filler: getDisplayFriendlySize(imageRestrictions.maxImageSize)
								}
							]
						})
					);
				}

				onSelect(
					{
						kind: HubItemElementKind.Image,
						id: null,
						image: {
							isSet: true,
							value: {
								filename: file.name,
								uri: dataUrl,
								[fileMetadataSymbol]: {
									size: file.size,
									mimeType: file.type
								},
								attribution
							}
						}
					}
				);
			} catch(e) {
				console.error(e);
			}

		},
		[
			alertManager,
			closeContainingDropOver,
			imageRestrictions.maxImageSize,
			onSelect,
			onSelectImage
		]
	);

	const selectVideoHandler = useCallback(
		async (): Promise<void> => {
			closeContainingDropOver?.();

			const selectResult: TryResult<string> = await onOpenVideoInputDialog({
				videoUrl: ""
			});

			if (selectResult.wasSuccessful) {
				onSelect({
					kind: HubItemElementKind.Video,
					id: null,
					url: selectResult.result
				});
			}
		},
		[
			closeContainingDropOver,
			onOpenVideoInputDialog,
			onSelect
		]
	);

	const selectEmbedHandler = useCallback(
		async (): Promise<void> => {
			closeContainingDropOver?.();

			const selectResult: TryResult<string> = await onOpenEmbedInputDialog({
				code: ""
			});

			if (selectResult.wasSuccessful) {
				onSelect({
					kind: HubItemElementKind.Embed,
					id: null,
					code: selectResult.result
				});
			}
		},
		[
			onSelect,
			onOpenEmbedInputDialog,
			closeContainingDropOver
		]
	);

	const selectLineHandler = useCallback(
		(): void => {
			closeContainingDropOver?.();

			onSelect({
				kind: HubItemElementKind.HorizontalLine,
				id: null
			});
		},
		[onSelect, closeContainingDropOver]
	);

	const selectFAQHandler = useCallback(
		(): void => {
			closeContainingDropOver?.();

			onSelect({
				kind: HubItemElementKind.FrequentlyAskedQuestion,
				id: null,
				items: [
					setClientId({
						question: "",
						answer: ""
					}),
					setClientId({
						question: "",
						answer: ""
					})
				]
			});
		},
		[closeContainingDropOver, onSelect]
	);

	const viewChangeHandler = useCallback(
		(view: ViewState): void => {
			onMenuItemsFocusReset();
			setView(view);
		},
		[onMenuItemsFocusReset]
	);

	switch (view) {
		case "default":
			return(
				<MenuList trackingId="element-picker">
					<MenuList.Item
						image={<ElementIcon value="text" />}
						title={localize("_Subtitle")}
						onClick={selectSubtitleHandler}
						trackingId="subtitle"
						index={0}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="paragraph" />}
						title={localize("_Paragraph")}
						onClick={selectParagraphHandler}
						trackingId="paragraph"
						index={1}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="image" />}
						title={localize("_Image")}
						onClick={() => viewChangeHandler("image-source")}
						trackingId="image"
						index={2}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="video" />}
						title={localize("_Video")}
						onClick={selectVideoHandler}
						trackingId="video"
						index={3}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="code" />}
						title={localize("_Embed")}
						onClick={selectEmbedHandler}
						trackingId="embed"
						index={4}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="minus" />}
						title={localize("_Line")}
						onClick={selectLineHandler}
						trackingId="line"
						index={5}
						tabbableIndex={tabbableIndex}
					/>
					<MenuList.Item
						image={<ElementIcon value="circle-question" />}
						title={localize("_FAQ")}
						onClick={selectFAQHandler}
						trackingId="faq"
						index={6}
						tabbableIndex={tabbableIndex}
					/>
				</MenuList>
			);
		case "image-source":
			return (
				<SelectImageMenu
					trackingId="select-image-menu"
					onSelectImage={selectImageHandler}
					onBack={() => viewChangeHandler("default")}
					title={localize("_SelectImage")}
					tabbableIndex={tabbableIndex}
					isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
				/>
			);
		default:
			never(view);
	}
};
ElementPicker.displayName = "ElementPicker";

type ViewState =
	| "default"
	| "image-source";

type ElementIconProps = {
	value: IconValue;
};

const ElementIcon = (props: ElementIconProps): ReactElement => {
	const {
		value
	} = props;

	return <Icon value={value} size="large" />;
};
ElementIcon.displayName = "ElementIcon";